import { UseQueryOptions, useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "@contexts/QueryClientContext";
import { useMemo } from "react";
import { getDestinationWeddingRegions, IDestinationWeddingRegion } from "@api/getregions";
import { QUERY_KEY_USER_DATA } from "@hooks/useUser";

const API_URL = '/api/easydirectoryapi/get-regions-grouped';

export type BookingType = {
    isMasterVenue: boolean;
    competitionStatusId: number;
    isCompetitionEligible: boolean;
    supplierName: string;
    supplierImage?: string;
    supplierSlug?: string;
    averageReviewRating: number;
    reviewCount: number;
    city?: string;
    state?: string;
    priceTier: number;
    itemType: number;
    weddingPlannerId: number;
    price?: number;
    note?: string;
    contactPerson?: string;
    phone?: string;
    email?: string;
    supplierId?: number;
    supplierCategoryId: number;
  };

export type RegionType = {
    id: number,
    name: string,
    slug: string,
}

export type RegionWithStateIdType = RegionType & {stateId: number}

export type StateType = {
    id: number,
    name: string,
    regions: RegionType[]
}
type ServerStateType = {
    StateId: number,
    StateName: string,
    Regions: {
        Id: number,
        Name: string,
        Slug: string,
    }[]
}

export function mapfetchStatesAndRegions(data: ServerStateType[]): StateType[] {
    return data.map((state) => ({
        id: state.StateId,
        name: state.StateName,
        regions: state.Regions?.map(region => ({
            id: region.Id,
            name: region.Name,
            slug: region.Slug,
        })) || []
    }));
}
export const fetchStatesAndRegions = async (): Promise<ServerStateType[]> => {
    const response = await fetch(API_URL);
    if (!response.ok) {
        throw new Error('Network response was not ok');
    }
    return await response.json() as ServerStateType[];
}

export const QUERY_KEY_ALL_REGIONS = ['all-regions'];

export const useAllRegions = (options?: Partial<UseQueryOptions<ServerStateType[]>>) => {
    return useQuery({
        refetchOnMount: false,
        staleTime: 1000 * 60 * 60 * 24 * 30,
        ...options,
        queryKey: QUERY_KEY_ALL_REGIONS,
        queryFn: fetchStatesAndRegions,
        select: mapfetchStatesAndRegions,
    });
}

// Helpers
export const useRegionsStatesMap = (options?: Partial<UseQueryOptions<ServerStateType[]>>) => {
    const { data, ...regionsQuery } = useAllRegions(options);
    const regionsMap = useMemo(() => {
        if(regionsQuery.isFetched && data) {
            return data.reduce((acc, state) => {
                state.regions.forEach(region => {
                    acc[region.id] = {
                        ...region,
                        stateId: state.id,
                    };
                });
                return acc;
            }, {} as Record<number, RegionWithStateIdType>);
        }
    }, [data]);
    const statesMap = useMemo(() => {
        if(regionsQuery.isFetched && data) {
            return data.reduce((acc, state) => {
                acc[state.id] = state;
                return acc;
            }, {} as Record<number, StateType>);
        }
    }, [data]);
    return {
        regionsMap,
        statesMap,
        data,
        ...regionsQuery,
    }
}

export const getRegionAndStateById = (
    regionsMap: Record<number, RegionWithStateIdType>,
    statesMap: Record<number, StateType>,
    regionId: number
) => {
    return mapRegionAndStateToRecord(regionsMap, statesMap)({ regionId });
}

export const mapRegionAndStateToRecord = <T extends {regionId: number}>(
    regionsMap: Record<number, RegionWithStateIdType>,
    statesMap: Record<number, StateType>
) => (entity: T): T & { region: RegionType, state: StateType } => {
    const region = regionsMap?.[entity.regionId];
    const state = statesMap?.[region?.stateId];
    return {
        ...entity,
        region,
        state,
    }
};

export const useDestinationWeddingRegion = (options?: Partial<UseQueryOptions<IDestinationWeddingRegion[]>>) => {
    return useQuery<IDestinationWeddingRegion[]>({
        refetchOnMount: false,
        staleTime: 1000 * 60 * 60 * 24 * 30,
        ...options,
        queryKey: ['destination-wedding-region'],
        queryFn: getDestinationWeddingRegions,
    });
}

export const useUpdateUserRegionData = (
    onSuccess: (() => void) | null = null
) => {
    return useMutation({
        mutationFn: updateUserRegionData,
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: QUERY_KEY_USER_DATA});
            queryClient.invalidateQueries({queryKey: ['dashboardSummary']});
            onSuccess && onSuccess();
        }
    })
}

const updateUserRegionData = async ({ regionId, dwRegionId, isDestinationWedding }: UserRegionData): Promise<string> => {
    const res = await fetch(`/api/dashboardapi/update-user-region-dw?regionId=${regionId ?? -1}&dwRegionId=${dwRegionId ?? -1}&isDW=${isDestinationWedding}`, {
        method: 'POST',
    });
    return await res.text();
}

export type UserRegionData = {
    regionId: number | undefined,
    dwRegionId: number | undefined,
    isDestinationWedding: boolean,
};